import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldWarning, ArrowClockwise, Plus, X, Warning,
  Buildings, Trash,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * BrokerCredentialsCard — dashboard-style card that renders the pilot's
 * on-file broker credential verifications. Sits above the Customer Credit
 * table so pilots see BOTH sides of a broker relationship: "will they pay?"
 * (credit) and "are they even authorized?" (credentials).
 *
 * Auto-hides when the user has zero verifications and no invoices yet — new
 * accounts don't need clutter. Once the pilot books their first customer,
 * we surface a persistent "verify your first broker" call-to-action.
 */

function StatusPill({ credential }) {
  if (!credential) return null;
  const isBad = credential.out_of_service || !credential.verified;
  const label = credential.verified
    ? (credential.out_of_service ? "OUT OF SERVICE" : credential.operating_status || "VERIFIED")
    : "NOT FOUND";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
        isBad
          ? "bg-red-500/15 text-red-300 border border-red-500/30"
          : credential.is_stale
          ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
          : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      }`}
    >
      {isBad ? <Warning size={10} weight="fill" /> : <ShieldCheck size={10} weight="fill" />}
      {label}
    </span>
  );
}

function VerifyModal({ open, onClose, onVerified, prefillName = "" }) {
  const [name, setName] = useState(prefillName);
  const [mc, setMc] = useState("");
  const [dot, setDot] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setName(prefillName); setMc(""); setDot(""); } }, [open, prefillName]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Broker name is required"); return; }
    if (!mc && !dot) { toast.error("Provide either MC# or DOT# to verify"); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/broker-credentials/verify", {
        customer_name: name.trim(),
        mc_number: mc.trim() || undefined,
        dot_number: dot.trim() || undefined,
      });
      if (data.out_of_service) {
        toast.warning(`${data.legal_name || name} is OUT OF SERVICE. Proceed with caution.`);
      } else if (!data.verified) {
        toast.error(data.verification?.reason || "Not found in FMCSA records");
      } else {
        toast.success(`Verified · ${data.legal_name || name} · ${data.operating_status}`);
      }
      onVerified && onVerified(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="verify-broker-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="verify-broker-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F]">
              Verify broker
            </div>
            <h2 className="mt-1 text-xl font-bold text-white">FMCSA credential check</h2>
            <div className="mt-1 text-xs text-white/50">
              Cross-references the FMCSA public census to confirm authority + operating status.
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-white/50 hover:bg-white/5 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-white/60">
              Broker name (label)
            </label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Acme Brokerage" required
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#FF5A1F] focus:outline-none"
              data-testid="verify-broker-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/60">MC #</label>
              <input
                type="text" value={mc} onChange={(e) => setMc(e.target.value)}
                placeholder="MC-79658 or 79658"
                className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#FF5A1F] focus:outline-none"
                data-testid="verify-broker-mc-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/60">DOT #</label>
              <input
                type="text" value={dot} onChange={(e) => setDot(e.target.value)}
                placeholder="125550"
                className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#FF5A1F] focus:outline-none"
                data-testid="verify-broker-dot-input"
              />
            </div>
          </div>
          <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] text-white/50">
            Provide either MC# or DOT#. When both are set we cross-validate they belong to the same carrier.
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={busy}
              className="rounded-lg border border-white/10 bg-transparent px-4 py-2 text-sm font-medium text-white/70 hover:bg-white/5">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90 disabled:opacity-50"
              data-testid="verify-broker-submit"
            >
              <ShieldCheck size={16} weight="fill" />
              {busy ? "Checking…" : "Run FMCSA check"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function BrokerCredentialsCard() {
  const [creds, setCreds] = useState([]);
  const [flagged, setFlagged] = useState(0);
  const [stale, setStale] = useState(0);
  const [unverified, setUnverified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPrefill, setModalPrefill] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [credRes, unvRes] = await Promise.all([
        client.get("/broker-credentials"),
        client.get("/broker-credentials/report/unverified").catch(() => ({ data: { unverified: [] } })),
      ]);
      setCreds(credRes.data.credentials || []);
      setFlagged(credRes.data.flagged_count || 0);
      setStale(credRes.data.stale_count || 0);
      setUnverified(unvRes.data.unverified || []);
    } catch {
      // Silent — no card renders on error, just log
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await client.post("/broker-credentials/bulk-refresh", {});
      toast.success(`Refreshed ${data.refreshed} broker${data.refreshed !== 1 ? "s" : ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Remove this broker's verification record?")) return;
    try {
      await client.delete(`/broker-credentials/${id}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (loading) return null;

  return (
    <>
      <section
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
        data-testid="broker-credentials-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F] flex items-center gap-1.5">
              <ShieldCheck size={12} weight="fill" /> Broker credentials
            </div>
            <h2 className="mt-1 text-xl font-bold text-white">FMCSA verification vault</h2>
            <div className="mt-1 text-xs text-white/50 max-w-xl">
              Cross-reference each broker's MC/DOT against the FMCSA public census so an "Out of Service" carrier
              never sneaks onto your books.
              {flagged > 0 && (
                <span className="ml-2 text-red-300 font-semibold">
                  {flagged} flagged
                </span>
              )}
              {stale > 0 && (
                <span className="ml-2 text-amber-300 font-semibold">
                  {stale} stale
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {creds.length > 0 && (
              <button
                onClick={refreshAll}
                disabled={refreshing}
                data-testid="broker-credentials-refresh"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/70 hover:border-white/30 hover:text-white disabled:opacity-50"
              >
                <ArrowClockwise size={11} className={refreshing ? "animate-spin" : ""} />
                {refreshing ? "Refreshing…" : "Refresh all"}
              </button>
            )}
            <button
              onClick={() => setModalOpen(true)}
              data-testid="broker-credentials-verify-new"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5A1F] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#FF5A1F]/90"
            >
              <Plus size={11} weight="bold" />
              Verify broker
            </button>
          </div>
        </div>

        {unverified.length > 0 && (
          <div
            className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-3"
            data-testid="broker-credentials-unverified"
          >
            <div className="flex items-start gap-2 mb-2">
              <ShieldWarning size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-100/90">
                <b className="text-amber-200">
                  {unverified.length} broker{unverified.length !== 1 ? "s" : ""} not yet verified
                </b>{" "}
                — brokers who appear on your invoices or loads but have never had an FMCSA check.
                Click one to run it now.
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {unverified.slice(0, 8).map((u) => (
                <button
                  key={u.customer_name_norm}
                  onClick={() => { setModalPrefill(u.customer_name); setModalOpen(true); }}
                  data-testid={`broker-unverified-${u.customer_name_norm}`}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/[0.08] px-2.5 py-1 text-[11px] text-amber-100 hover:bg-amber-500/[0.15] hover:border-amber-400/50"
                >
                  {u.customer_name}
                  <span className="text-[9px] text-amber-300/70 ml-1">
                    {u.load_count + u.invoice_count} touch{(u.load_count + u.invoice_count) !== 1 ? "es" : ""}
                  </span>
                </button>
              ))}
              {unverified.length > 8 && (
                <span className="inline-flex items-center px-2 text-[11px] text-amber-200/60">
                  + {unverified.length - 8} more
                </span>
              )}
            </div>
          </div>
        )}

        {creds.length === 0 ? (
          <div className="mt-5 rounded-lg border border-dashed border-white/10 p-6 text-center">
            <Buildings size={20} className="mx-auto mb-2 text-white/40" />
            <div className="text-sm text-white/60">No verifications yet.</div>
            <div className="mt-1 text-xs text-white/40">
              Click Verify broker to run your first FMCSA check.
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="broker-credentials-list">
            {creds.map((c) => (
              <div
                key={c.id}
                className={`rounded-lg border p-3 ${
                  c.out_of_service || !c.verified
                    ? "border-red-500/30 bg-red-500/[0.05]"
                    : c.is_stale
                    ? "border-amber-500/25 bg-amber-500/[0.03]"
                    : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`broker-credential-row-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate">{c.customer_name}</div>
                    {c.legal_name && c.legal_name.toLowerCase() !== c.customer_name.toLowerCase() && (
                      <div className="text-[11px] text-white/50 truncate">
                        Legal: {c.legal_name}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteOne(c.id)}
                    className="p-1 text-white/40 hover:text-red-400"
                    title="Remove"
                    data-testid={`broker-credential-delete-${c.id}`}
                  >
                    <Trash size={12} />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <StatusPill credential={c} />
                  <span className="text-[10px] text-white/40 tabular-nums">
                    {c.days_since_verified !== null
                      ? `verified ${c.days_since_verified}d ago`
                      : "never verified"}
                  </span>
                </div>
                <div className="mt-2 text-[11px] text-white/60 space-y-0.5">
                  {c.mc_number && <div>MC #{c.mc_number}</div>}
                  {c.dot_number && <div>DOT #{c.dot_number}</div>}
                  {c.verification?.power_units > 0 && (
                    <div className="text-white/40">
                      {c.verification.power_units} power units · {c.verification.drivers} drivers
                    </div>
                  )}
                  {!c.verified && c.verification?.reason && (
                    <div className="text-red-300 mt-1">{c.verification.reason}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <VerifyModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setModalPrefill(""); }}
        onVerified={load}
        prefillName={modalPrefill}
      />
    </>
  );
}
